import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren
} from '@angular/core';
import {
  Leg,
  RFQ_STATUS_PENDING,
  RFQ_STATUS_RFE_PENDING,
  RFQ_STATUS_TRADED,
  ResultPerLeg,
  RfqBidOfferQuotes,
  RfqLegSettings,
  RfqQuote,
  RfqTab
} from '@approot/models';
import {
  IndicativeQuote,
  LegService,
  ProductsConfigService,
  RfqService,
  RfqTabService
} from '@approot/services';
import { getTabTitleCross } from '@approot/services/rfq/rfq-creation';
import {
  indicativeQuoteMaker,
  legComponentsToRfqBuilderLegs
} from '@approot/services/rfq/rfq-tab/rfq-tab';
import { quoteActionAnimation, tabStatus } from '@approot/shared';
import { SubscriptionSink } from '@approot/utilities';
import { isEqual } from 'lodash';
import { LegComponent } from '../strategy-detail/leg/leg.component';

type LegStringFields = 'cross' | 'currency' | 'premiumCurrency' | 'premiumType';
type CachedLeg = Record<LegStringFields, string> &
  Record<string, string | number>;

@Component({
  selector: 'app-direct-rfq',
  templateUrl: './direct-rfq.component.html',
  styleUrls: ['./direct-rfq.component.scss'],
  providers: [LegService, RfqTabService],
  animations: [tabStatus, quoteActionAnimation]
})
export class DirectRfqComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() public rfqTab: RfqTab;
  @Input() public index: number;
  @ViewChildren(LegComponent) public legComponents: QueryList<LegComponent>;
  public legValid = false;
  public rfqExecutionSettings: RfqLegSettings = {
    rfqLeg: true,
    directRfq: true,
    editableSettings: { editableFields: [], lockWhileActive: [] },
    static: false
  };
  public warningScreenData = {
    warningScreenOpen: false,
    messageToDisplay: '',
    headerMessage: ''
  };
  public indicativeQuoteBid: IndicativeQuote;
  public indicativeQuoteAsk: IndicativeQuote;
  public legPrice: ResultPerLeg;
  private cachedLeg: CachedLeg;
  private subscriptions = new SubscriptionSink();
  constructor(
    private legService: LegService,
    private proudctsConfigService: ProductsConfigService,
    private rfqService: RfqService,
    private rfqTabService: RfqTabService,
    private elementRef: ElementRef
  ) {
    this.rfqTabService.isDirectRfq = true;
    this.legService.attributesDisplayList =
      this.proudctsConfigService.productInfo.directRfqAttributesDisplayList;
    this.legService.productSpecifications =
      this.proudctsConfigService.productInfo.directRfqProducts;
  }
  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.rfqTabService.destroy();
  }

  public ngAfterViewInit(): void {
    this.rfqTabService.selectedLegs = this.legComponents.toArray();
    this.setRfqTabData();
    this.subscriptions.sink = this.legComponents.changes.subscribe(_ => {
      this.rfqTabService.selectedLegs = this.legComponents.toArray();
      this.setRfqTabData();
    });
  }

  public ngOnInit(): void {
    this.rfqTabService.tabData = this.rfqTab;
    this.legService.addLeg({
      productName: this.legService.productSpecifications
        .find(x => x.productType.shortName === 'vanillaoption')
        .productType.longName.replace(/\s+/g, '')
        .toLowerCase(),
      newProduct: false,
      preSetPosition: {},
      tradeViewMode: false
    });
  }

  public get legs(): Leg[] {
    return this.legService.legs;
  }

  public closeRfq(): void {
    this.rfqService.removeDirectRfqTab(this.index);
  }

  public onChangeInLegPrice(price: ResultPerLeg): void {
    this.legPrice = price;
    const firstLeg = this.legComponents?.first;
    const legForm = firstLeg?.form;
    this.legValid = !!legForm?.valid;

    if (price) {
      this.cachedLeg = legForm?.value;
      this.rfqTab.title.quotePair = getTabTitleCross(
        legComponentsToRfqBuilderLegs(this.legComponents.toArray())[0]
      );
      const priceResult = this.legPrice.pricing;
      const premiumCurrency = this.cachedLeg.premiumCurrency;
      const base = this.rfqTab.title.quotePair.base;
      const currency = this.cachedLeg.currency;
      const premiumType = this.cachedLeg.premiumType;
      if (firstLeg?.leg.productType === 'newdcd') {
        // for dcds the ask response is set as the bid indicative quote
        this.indicativeQuoteBid = indicativeQuoteMaker(
          'ask',
          priceResult,
          premiumCurrency,
          base,
          premiumType
        );
        this.indicativeQuoteBid.bidAsk.type = null;
        return;
      }
      this.indicativeQuoteAsk = indicativeQuoteMaker(
        'ask',
        priceResult,
        premiumCurrency,
        base,
        premiumType
      );
      this.indicativeQuoteBid = indicativeQuoteMaker(
        'bid',
        priceResult,
        premiumCurrency,
        base,
        premiumType
      );
    } else {
      this.indicativeQuoteBid = null;
      this.indicativeQuoteAsk = null;
    }
  }

  public onChangeInLegs(): void {
    const legComponent = this.legComponents?.first;
    if (
      legComponent &&
      legComponent.form.get('productType').value ===
        legComponent.initialProductType
    ) {
      this.legValid = legComponent.form.valid;
      const legValue = legComponent.form.value;

      if (this.rfqTab.title.status.statusType === RFQ_STATUS_TRADED) {
        this.rfqTabService.changeTabTile(null, null);
        this.removeAllQuotes();
      }

      if (
        this.legValid &&
        (!this.legPrice || !isEqual(legValue, this.cachedLeg)) &&
        !legComponent.form.pending
      ) {
        if (legValue.strike === undefined || !isNaN(legValue.strike)) {
          const activeElement = document.activeElement;
          const focusedAttributeId = activeElement?.id;
          if (focusedAttributeId) {
            // we need to disallow pricing on a field that has changed but is stil focused
            const directRfqFocuedAttribute = (
              this.elementRef.nativeElement as HTMLElement
            ).querySelector(
              `.direct-rfq__legs-container .leg-container #${focusedAttributeId}`
            );
            if (
              activeElement === directRfqFocuedAttribute &&
              legValue[focusedAttributeId] !==
                this.cachedLeg?.[focusedAttributeId]
            ) {
              return;
            }
          }
          if (legComponent.leg.disablePricingButton) {
            legComponent.legPricingService.pricingSub.unsubscribe();
          }
          legComponent.legPricingService.price(true);
          this.cachedLeg = legValue;
        }
      }
    }
  }

  private setRfqTabData() {
    setTimeout(() => {
      this.rfqService.setRfqTabGenericInfo(
        this.rfqTab,
        legComponentsToRfqBuilderLegs(this.rfqTabService.selectedLegs),
        false
      );
    }, 0);
  }

  public startRfq(): void {
    // this.rfqTab.legs = this.rfqService.rfqLegs(this.rfqTabService.selectedLegs);
    // this.rfqTabService.setAllAttributesReadOnlyState(true);
    // this.rfqTabService
    //   .createDTO()
    //   .pipe(
    //     switchMap(_ => {
    //       return this.rfqTabService.initRfqSubmitRequest();
    //     })
    //   )
    //   .subscribe({
    //     next: _ => {
    //       this.rfqTabService.startTimer();
    //     },
    //     error: _ => {
    //       this.rfqTabService.changeTabTile(RFQ_STATUS_ERROR, null);
    //       this.rfqTabService.setAllAttributesReadOnlyState(false);
    //       this.rfqTabService.wipeAll();
    //     }
    //   });
  }

  public cancelRfq(): void {
    this.rfqTabService.wipeAll();
    this.removeAllQuotes();
  }

  public get mainTimer() {
    return this.rfqTabService.mainTimer;
  }

  public get isRfqRunning(): boolean {
    return this.rfqTabService.rfqRunning();
  }

  public get activeExtraAttributesLength(): number {
    return this.legService.activeExtraAttributes.length || 0;
  }

  public get quotes(): RfqBidOfferQuotes {
    return this.rfqTabService.quotesBeingDisplayed;
  }

  public get frozenQuotes(): RfqBidOfferQuotes {
    return this.rfqTabService.frozenQuotes;
  }

  public get currentTimestamp(): number {
    return this.rfqService.currentTimestamp;
  }

  public warningScreenResponse(val = false): void {
    this.warningScreenData.warningScreenOpen = val;
  }

  public rfeRequest(quote: RfqQuote): void {
    this.rfqTabService.changeTabTile(RFQ_STATUS_PENDING, null);
    this.rfqTabService.wipeAllTimers(); // when a request is sent, all the row timers should be wiped
    this.rfqTabService.executedSide = quote.type;
    this.startRfeTimer();
    this.rfqTabService.sendRfeAction(quote).subscribe({
      error: (err: HttpErrorResponse) => {
        this.rfqTabService.frozenQuotes = null;
        this.rfqTabService.rfqRunning.update(_ => true);
        this.rfqTabService.clearRfeRequestInfo();
        this.warningScreenData.messageToDisplay =
          'An error occurred with the Rfe request';
        this.warningScreenData.headerMessage = 'Issue with the requested rfe';
        this.warningScreenData.warningScreenOpen = true;
        if (err.error?.exception?.type === 'QuoteExpiredException') {
          this.warningScreenData.messageToDisplay =
            'Quote has expired, please try again';
        }
        this.rfqTabService.startTimer();
        this.rfqTabService.clearRfeTimer();
      }
    });
  }

  private removeAllQuotes(): void {
    this.rfqTabService.quotes.bids = [];
    this.rfqTabService.quotes.offers = [];
    this.rfqTabService.frozenQuotes = null;
  }

  private startRfeTimer(): void {
    this.rfqTabService.rfeTimer = setTimeout(() => {
      this.rfqTabService.tabData.title.status.statusType =
        RFQ_STATUS_RFE_PENDING;
      this.rfqTabService.statusTypeUpdated.set(RFQ_STATUS_RFE_PENDING);
      this.warningScreenData.warningScreenOpen = true;
      this.warningScreenData.headerMessage =
        'RFE is taking longer than expected';
      this.warningScreenData.messageToDisplay =
        'No response on RFE. Please contact support';
    }, 60_000);
  }
}
